'use client';

import { Suspense } from 'react';
import { DownOutlined, UserOutlined } from '@ant-design/icons';
import type { StockSymbol } from '@benzinga/session';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import { BenzingaProLogo } from '@benzinga/logos-ui';
import { BzEdgeLight } from '@benzinga/icons';
import { AccountMenu } from '../AccountMenu';
import { getPremiumServiceHref, type PremiumServiceAccess } from '../../premiumServiceLinks';
import { LocaleMenu } from './LocaleMenu';
import { MarketTicker } from './MarketTicker';
import styles from './QuotesAndAccountBar.module.scss';

export interface QuotesAndAccountBarProps {
  isLoggedIn: boolean;
  onLogin: () => void;
  onRegister?: () => void;
  onLogout: () => void;
  premiumAccess: PremiumServiceAccess;
  quotes?: Record<StockSymbol, DelayedQuote> | null;
  marketTickers?: string[];
  recentTickers?: string[];
  navChromeReady?: boolean;
}

const AccountBootButton = () => (
  <div className="account-menu menu-wrapper">
    <button className="menu-button" type="button">
      <UserOutlined />
      <span className="my-account-text">My Account</span>
      <DownOutlined />
    </button>
  </div>
);

export const QuotesAndAccountBar = ({
  isLoggedIn,
  onLogin,
  onRegister,
  onLogout,
  premiumAccess,
  quotes,
  marketTickers,
  navChromeReady = false,
}: QuotesAndAccountBarProps) => {
  const desktopIsEdge = !premiumAccess.hasPro && premiumAccess.hasEdge;
  const showAnon = !navChromeReady || !isLoggedIn;
  const showAuthed = !navChromeReady || isLoggedIn;
  const showEdgeLogo = !navChromeReady || desktopIsEdge;
  const showProLogo = !navChromeReady || !desktopIsEdge;

  return (
    <div className="relative z-[950] border-b border-[var(--hubnav-line)] bg-[var(--hubnav-util-bg)] text-[13px] text-[var(--hubnav-util-fg)]">
      <div className="mx-auto flex min-h-[38px] w-full max-w-[var(--hubnav-max-width)] items-center gap-[22px] px-[var(--hubnav-gutter)]">
        <div className="min-w-0 flex-1 overflow-hidden max-[980px]:hidden">
          <MarketTicker quotes={quotes} tickers={marketTickers} />
        </div>

        <div className={`${styles.account} ml-auto flex items-center gap-3 whitespace-nowrap max-[980px]:ml-0 max-[980px]:w-full`}>
          {showEdgeLogo ? (
            <a
              className="mr-2 inline-flex items-center max-[980px]:hidden [&_svg]:h-[13px] [&_svg]:w-auto"
              data-nav-boot="edge"
              href={getPremiumServiceHref('edge', premiumAccess)}
              aria-label="Benzinga Edge"
            >
              <BzEdgeLight className="h-3 w-auto" />
            </a>
          ) : null}
          {showProLogo ? (
            <a
              className="mr-2 inline-flex items-center max-[980px]:hidden [&_svg]:h-[13px] [&_svg]:w-auto"
              data-nav-boot="pro"
              href={getPremiumServiceHref('pro', premiumAccess)}
              aria-label="Benzinga Pro"
            >
              <BenzingaProLogo className="h-[13px] w-auto" variant="blue" />
            </a>
          ) : null}

          <a
            className="hidden items-center max-[980px]:mr-auto max-[980px]:inline-flex [&_svg]:h-3 [&_svg]:w-auto"
            href={getPremiumServiceHref('edge', premiumAccess)}
            aria-label="Benzinga Edge"
          >
            <BzEdgeLight className="h-3 w-auto" />
          </a>

          <LocaleMenu />

          {showAnon ? (
            <div className="flex items-center gap-1" data-nav-boot="anon">
              <button
                className="inline-flex h-[26px] cursor-pointer items-center rounded-md border-0 bg-transparent px-2.5 font-[inherit] text-[13px] font-semibold text-[var(--hubnav-util-fg)] transition-[background-color,color,transform] duration-150 ease-out hover:bg-white/10 hover:text-white active:scale-[0.96] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--hubnav-accent)]"
                type="button"
                onClick={onLogin}
              >
                Sign in
              </button>
              {onRegister ? (
                <button
                  className="inline-flex h-[26px] cursor-pointer items-center rounded-md border-0 bg-[#cf440d] px-2.5 font-[inherit] text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] transition-[background-color,color,transform] duration-150 ease-out hover:bg-[#ba3d0a] active:scale-[0.96] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--hubnav-accent)]"
                  type="button"
                  onClick={onRegister}
                >
                  Register
                </button>
              ) : null}
            </div>
          ) : null}
          {showAuthed ? (
            <div className="flex items-center gap-1" data-nav-boot="authed">
              <Suspense fallback={<AccountBootButton />}>
                <AccountMenu deviceType="desktop" isLoggedIn navChromeReady={navChromeReady} onLogout={onLogout} />
              </Suspense>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
